export type PersistedSliceConfig<T> = {
  key: string;
  fields: readonly (keyof T & string)[];
  // 未指定の場合は localStorage
  storage?: "local" | "session";
  // 初期値が undefined のフィールドは、指定がなければ文字列として扱う
  fieldTypes?: Partial<Record<keyof T & string, "boolean" | "number">>;
  // 指定した場合は保存時刻も保存し、この時間を過ぎた保存データは復元せずに消す
  maxAgeMs?: number;
  // 指定した版の redux-persist の保存データを読み、許可フィールドだけ現行形式へ移す
  legacyReduxPersistVersion?: number;
  legacyReduxPersistKey?: string;
  legacyReduxPersistSavedAt?: (state: T) => number | undefined;
  sanitize?: (state: T) => T;
};

const SAVED_AT_KEY = "_savedAt";

const getStorage = (storage: PersistedSliceConfig<unknown>["storage"]) =>
  storage === "session" ? window.sessionStorage : window.localStorage;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasSameType = (expected: unknown, value: unknown, optionalType = "string") => {
  if (expected === undefined) return typeof value === optionalType;
  if (Array.isArray(expected)) return Array.isArray(value);
  if (isRecord(expected)) return isRecord(value);
  return typeof value === typeof expected;
};

const isExpired = (maxAgeMs: number | undefined, parsed: Record<string, unknown>) => {
  if (maxAgeMs === undefined) return false;
  const savedAt = parsed[SAVED_AT_KEY];
  // 端末の時計を戻した場合などに未来の日時で期限が効かなくならないよう、未来の日時も期限切れとして扱う
  return typeof savedAt !== "number" || savedAt > Date.now() || Date.now() - savedAt > maxAgeMs;
};

// 読めない保存データ(壊れている、型が合わない、期限切れ)は undefined を返す
const parsePersistedSlice = <T extends object>(config: PersistedSliceConfig<T>, initial: T, serialized: string) => {
  try {
    const parsed: unknown = JSON.parse(serialized);
    if (!isRecord(parsed)) return undefined;
    const isLegacy = config.legacyReduxPersistVersion !== undefined && "_persist" in parsed;
    if (isLegacy) {
      if (typeof parsed._persist !== "string") return undefined;
      const metadata: unknown = JSON.parse(parsed._persist);
      if (
        !isRecord(metadata) ||
        metadata.version !== config.legacyReduxPersistVersion ||
        metadata.rehydrated !== true
      ) {
        return undefined;
      }
    }
    const target = initial as Record<string, unknown>;
    // 途中のフィールドが壊れていた場合に一部だけ復元しないよう、すべて読めてから反映する。
    const restored: Record<string, unknown> = {};
    for (const key of config.fields) {
      if (!(key in parsed)) continue;
      let value = parsed[key];
      if (isLegacy) {
        if (typeof value !== "string") return undefined;
        value = JSON.parse(value);
      }
      if (!hasSameType(target[key], value, config.fieldTypes?.[key])) return undefined;
      restored[key] = value;
    }
    const state = { ...initial, ...restored };
    const savedAt = isLegacy ? config.legacyReduxPersistSavedAt?.(state) : parsed[SAVED_AT_KEY];
    if (isExpired(config.maxAgeMs, { [SAVED_AT_KEY]: savedAt })) return undefined;
    return { state, isLegacy, savedAt: typeof savedAt === "number" ? savedAt : undefined };
  } catch {
    return undefined;
  }
};

export const readPersistedSlice = <T extends object>(config: PersistedSliceConfig<T>, initial: T): T => {
  try {
    if (typeof window === "undefined") return initial;
    const storage = getStorage(config.storage);
    let sourceKey = config.key;
    let serialized = storage.getItem(sourceKey);
    // 現行データがある場合は優先し、古い状態を復活させない
    if (serialized === null && config.legacyReduxPersistKey) {
      sourceKey = config.legacyReduxPersistKey;
      serialized = storage.getItem(sourceKey);
    }
    if (!serialized) return initial;
    const restored = parsePersistedSlice(config, initial, serialized);
    // 読めない保存データは、個人情報を含む古いデータが残り続けないよう消す
    if (!restored || (sourceKey !== config.key && !restored.isLegacy)) {
      storage.removeItem(sourceKey);
      if (config.legacyReduxPersistKey && config.legacyReduxPersistKey !== sourceKey) {
        storage.removeItem(config.legacyReduxPersistKey);
      }
      return initial;
    }
    const state = config.sanitize ? config.sanitize(restored.state) : restored.state;
    // 有効期限は延長せず、旧形式と保存対象外の個人情報を除く
    if (restored.isLegacy || config.sanitize) {
      const saved = writePersistedSlice(config, state, restored.savedAt);
      if (saved && config.legacyReduxPersistKey && config.legacyReduxPersistKey !== config.key) {
        storage.removeItem(config.legacyReduxPersistKey);
      }
    } else if (config.legacyReduxPersistKey && config.legacyReduxPersistKey !== config.key) {
      storage.removeItem(config.legacyReduxPersistKey);
    }
    return state;
  } catch {
    // storage が利用できない場合も起動する。
    return initial;
  }
};

export const writePersistedSlice = <T extends object>(
  config: PersistedSliceConfig<T>,
  state: T,
  savedAt = Date.now()
) => {
  try {
    const values = (config.sanitize ? config.sanitize(state) : state) as Record<string, unknown>;
    const entries = config.fields.filter((key) => values[key] !== undefined).map((key) => [key, values[key]]);
    if (config.maxAgeMs !== undefined) entries.push([SAVED_AT_KEY, savedAt]);
    const serialized = JSON.stringify(Object.fromEntries(entries));
    const storage = getStorage(config.storage);
    if (storage.getItem(config.key) !== serialized) storage.setItem(config.key, serialized);
    return true;
  } catch {
    // メモリ上の状態で利用を続ける。
    return false;
  }
};

// persist の初回復元前は子 effect などによる初期値の保存を抑止する。
export const createPersistedSliceStorage = <T extends object>(
  config: PersistedSliceConfig<T> | undefined,
  initial: T
) => {
  let restored = false;
  return {
    getItem: (_name: string) => {
      const state = config ? readPersistedSlice(config, initial) : initial;
      restored = true;
      return { state };
    },
    setItem: (_name: string, value: { state: T }) => {
      if (config && restored) writePersistedSlice(config, value.state);
    },
    removeItem: (_name: string) => {
      if (!config) return;
      try {
        getStorage(config.storage).removeItem(config.key);
      } catch {
        // storage が利用できない場合もメモリ上の状態で継続する。
      }
    },
  };
};

type PersistableStore<T> = {
  getState: () => T;
  subscribe: (listener: (state: T, prevState: T) => void) => () => void;
};

// 別タブが保存した値を上書きしないよう、対象のフィールドが変わったときだけ書き込む。
// restored は復元直後の状態で、購読前に変わっていた分もここで保存する。
export const subscribePersistedSlice = <T extends object, S extends T>(
  store: PersistableStore<S>,
  config: PersistedSliceConfig<T>,
  restored: T
) => {
  const isChanged = (state: T, prevState: T) => config.fields.some((key) => state[key] !== prevState[key]);
  if (isChanged(store.getState(), restored)) writePersistedSlice(config, store.getState());
  return store.subscribe((state, prevState) => {
    if (isChanged(state, prevState)) writePersistedSlice(config, state);
  });
};
